import React from 'react';
import type {
  ChamberAxisControllerModel,
  ChamberAxisPickMode,
} from './chamberAxisVisualController';

type ChamberAxisControllerProps = {
  model: ChamberAxisControllerModel;
  onPickModeChange: (pickMode: ChamberAxisPickMode) => void;
  onCalculate: () => void;
  onReset: () => void;
};

const formatWorldPoint = (point: [number, number, number] | undefined) =>
  point ? point.map((value) => value.toFixed(1)).join(', ') : 'not set';

export function ChamberAxisController({
  model,
  onPickModeChange,
  onCalculate,
  onReset,
}: ChamberAxisControllerProps) {
  if (!model.visible) return null;

  return (
    <div
      data-testid="chamber-axis-controller"
      style={{
        position: 'absolute',
        top: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 8px',
        border: '1px solid rgba(255, 255, 255, 0.32)',
        borderRadius: 6,
        background: 'rgba(10, 12, 16, 0.82)',
        color: '#fff',
        fontSize: 12,
        lineHeight: 1,
        pointerEvents: 'auto',
        zIndex: 4,
      }}
    >
      <span style={{ fontWeight: 600 }}>Axis Vector</span>
      <AxisButton
        label="Start"
        ariaLabel="Pick chamber axis start point"
        active={model.activePickMode === 'pickingStart'}
        title={`Start: ${formatWorldPoint(model.startWorld)}`}
        onClick={() => onPickModeChange('pickingStart')}
      />
      <AxisButton
        label="End"
        ariaLabel="Pick chamber axis end point"
        active={model.activePickMode === 'pickingEnd'}
        title={`End: ${formatWorldPoint(model.endWorld)}`}
        onClick={() => onPickModeChange('pickingEnd')}
      />
      <AxisButton
        label="Calc"
        ariaLabel="Apply chamber axis vector"
        disabled={!model.canCalculate}
        title={model.error ? `Cannot calculate: ${model.error}` : undefined}
        onClick={onCalculate}
      />
      <AxisButton
        label="Reset"
        ariaLabel="Reset chamber axis vector"
        onClick={onReset}
      />
    </div>
  );
}

function AxisButton({
  label,
  ariaLabel,
  active = false,
  disabled = false,
  title,
  onClick,
}: {
  label: string;
  ariaLabel: string;
  active?: boolean;
  disabled?: boolean;
  title?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      aria-label={ariaLabel}
      disabled={disabled}
      title={title}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        if (!disabled) onClick();
      }}
      style={{
        border: active
          ? '1px solid rgba(255, 255, 255, 0.92)'
          : '1px solid rgba(255, 255, 255, 0.24)',
        borderRadius: 4,
        background: active
          ? 'rgba(255, 255, 255, 0.22)'
          : 'rgba(255, 255, 255, 0.08)',
        color: disabled ? 'rgba(255, 255, 255, 0.42)' : '#fff',
        cursor: disabled ? 'default' : 'pointer',
        font: 'inherit',
        minWidth: 46,
        padding: '5px 7px',
      }}
    >
      {label}
    </button>
  );
}
